/* eslint-disable max-lines */
/* eslint-disable no-console */
import { ICancellationItem } from '../models/cancel-items';
import {
  IAnalyticsEvent,
  IAnalyticsEventWithFulfillment,
  IPageData,
  TDigitalData,
  IEventInfo,
  TEventCategory,
  IError,
  TErrorTypes,
  TErrorDisplayTypes,
  IPostOrderItems,
  ICategory,
} from '../models/analytics';
import { getWindow } from './general';
import { ERROR_MESSAGE } from '../components/order-details/returns/Soris/CancelConfirmationDrawer';
import { IOrderDetailsResponse } from '../models/order-details';
import { getMilitaryExchange } from './returns/returns-helper';

/**
 * Function for updating page data in the global variable _T
 * @param digitalData - the digital data object we want to push to page data array
 * @returns undefined
 */
export const setAnalytics = (digitalData: TDigitalData) => {
  const _ddo = digitalData.init();

  if (getWindow()) {
    window._T = window._T || {};

    if (typeof window._T.pageData === 'undefined') {
      window._T.pageData = [];
    }

    window._T.pageData.push(_ddo);
  }
};

/**
 * Function for updating an event array in the global variable digitalData
 * @param analyticsEvent - an event object we want to push to event array
 * @returns undefined
 */
export const pushEventInfo = (analyticsEvent: IAnalyticsEvent) => {
  if (analyticsEvent && getWindow()?.digitalData) {
    try {
      window.digitalData.event = getWindow()?.digitalData.event || [];
      getWindow()?.digitalData.event.push(analyticsEvent);
    } catch (err) {
      console.error('DDO : Error in pushing the event info!!!');
    }
  }
};

export const pushEventInfoWithFulfillment = (
  analyticsEvent: IAnalyticsEventWithFulfillment,
) => {
  if (analyticsEvent && getWindow()?.digitalData) {
    try {
      window.digitalData.event = getWindow()?.digitalData.event || [];
      getWindow()?.digitalData.event.push(analyticsEvent);
    } catch (err) {
      console.error(
        'DDO : Error in pushing the event info with fulfillment type!!!',
      );
    }
  }
};

const setEvent = (
  eventCategory: TEventCategory,
  eventInfo: IEventInfo,
  payload?: Record<string, unknown>,
) => {
  pushEventInfo({
    category: {
      primaryCategory: eventCategory,
    },
    eventInfo,
    ...payload,
  });
};

export const setPageInfo = (
  type: TErrorTypes,
  displayType: TErrorDisplayTypes,
  pageSection: string,
  message: string,
  code: string,
) => {
  pushPageErrorInfo({
    type,
    displayType,
    pageSection,
    message,
    code,
  });
};

export const pushPageErrorInfo = (pageError: IError) => {
  if (pageError && getWindow()?.digitalData) {
    window.digitalData.page.error = getWindow()?.digitalData.page.error || [];
    getWindow()?.digitalData.page.error.push(pageError);
    setAnalytics(window.digitalData);
  }
};

export type PostPageErrorType = 'system' | 'error' | 'advisory';

export const pushErrorEvent = (
  pageSection: string,
  errCode: string,
  errMessage: string,
  displayType = 'inline',
  type: PostPageErrorType = 'error',
) => {
  setEvent('error', {
    eventName: 'post page error',
    error: [
      {
        type,
        displayType,
        pageSection,
        code: errCode,
        message: errMessage,
      },
    ],
  });
};

export const setStashEvent = (
  eventCategory: TEventCategory,
  eventInfo: IEventInfo,
  eventMode: TEventCategory,
  payload?: Record<string, unknown>,
) => {
  pushEventInfo({
    category: {
      primaryCategory: eventCategory,
    },
    eventInfo,
    eventMode,
    ...payload,
  });
};

export const stashLinkEvent = (action: string) => {
  setStashEvent(
    'stash',
    {
      eventName: 'my account settings',
    },
    'stash',
    {
      stash: { myAccount: { settings: action } },
    },
  );
};

export const setOverlayEvent = (
  eventName: string,
  payload?: Record<string, unknown>,
) => {
  setEvent(
    'overlay',
    {
      eventName,
    },
    payload,
  );
};

export const createPostOrderItems = (
  cancellationItems: ICancellationItem[],
): IPostOrderItems[] => {
  return cancellationItems?.map((cancellationItem: ICancellationItem) => {
    return {
      fulfillment: {
        method:
          cancellationItem.fulfillmentType === 'MA'
            ? 'appl'
            : cancellationItem.fulfillmentType.toLowerCase(),
        store:
          cancellationItem.fulfillmentType === 'BOPIS' ||
          cancellationItem.fulfillmentType === 'BOSS'
            ? cancellationItem.storeNumber
            : undefined,
        zip:
          cancellationItem.fulfillmentType === 'MA' ||
          cancellationItem.fulfillmentType === 'BODFS'
            ? cancellationItem.zipCode
            : undefined,
      },
      price: {
        basePrice: (
          (cancellationItem.canceledQuantity ?? 0) *
          (cancellationItem?.unitPrice ?? 0)
        ).toFixed(2),
      },
      productInfo: {
        productName: cancellationItem.description,
        sku: cancellationItem.skuNumber,
        superSku: cancellationItem.superSku,
      },
      quantity: cancellationItem.canceledQuantity?.toString(),
    };
  });
};

/**
 * Function for updating the postOrder object in the global variable digitalData
 * @param postOrderItems - a postOrderItems array we want to set in the postOrder object in the digitalData
 * @param transactionID - a string we want to set in the postOrder object in the digitalData
 * @returns undefined
 */
export const setPostOrderInfo = (
  postOrderItems?: IPostOrderItems[],
  transactionID?: string,
  orderStatus?: string,
) => {
  if (getWindow()?.digitalData) {
    try {
      window.digitalData.postOrder = getWindow()?.digitalData.postOrder || {};
      window.digitalData.postOrder.transactionID = transactionID;
      window.digitalData.postOrder.item = postOrderItems;
      if (orderStatus) {
        window.digitalData.postOrder.orderStatus = orderStatus;
      }

      setAnalytics(getWindow()?.digitalData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'DDO : Error in setting the post order object and intializing it!!!',
      );
    }
  }
};

/**
 * Function for setting page details and transactionID (in postOrder object) in the global variable digitalData
 * @param page - a page details object we want to set in the digitalData
 * @param transactionID - a string we want to set in the postOrder object in the digitalData
 * @returns undefined
 */
export const setOrderDetailsPageInfo = (
  page: IPageData,
  transactionID: string,
  category: ICategory,
) => {
  if (getWindow()?.digitalData) {
    try {
      window.digitalData.page = {
        ...window.digitalData.page,
        ...page,
      };
      window.digitalData.postOrder = getWindow()?.digitalData.postOrder || {};
      window.digitalData.postOrder.transactionID = transactionID;
      window.digitalData.category = category;

      setAnalytics(getWindow()?.digitalData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'DDO : Error in setting the page and post order object and intializing it!!!',
      );
    }
  }
};

/**
 * Function for setting page details in the global variable digitalData
 * @param page - an page details object we want to set in the digitalData
 * @returns undefined
 */
export function setPageDetails(page: IPageData) {
  if (getWindow()?.digitalData) {
    try {
      window.digitalData.page = page;
      setAnalytics(getWindow()?.digitalData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'DDO : Error in setting the page object and initializing it!!!',
      );
    }
  }
}

export const setTrackBusinessSpendPageInfo = (
  page: IPageData,
  transactionID: string,
  category: ICategory,
) => {
  if (getWindow()?.digitalData) {
    try {
      window.digitalData.page = page;
      window.digitalData.postOrder = getWindow()?.digitalData.postOrder || {};
      window.digitalData.postOrder.transactionID = transactionID;
      window.digitalData.category = category;

      setAnalytics(getWindow()?.digitalData);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'DDO : Error in setting the page and post order object and intializing it!!!',
      );
    }
  }
};

export const setPageDetailsValues = (
  primaryCategory: string,
  subCategory1: string,
  subCategory2: string,
  pageName: string,
  pageType: string,
) => {
  const page: IPageData = {
    category: {
      primaryCategory,
      subCategory1,
      subCategory2,
    },
    pageInfo: {
      pageName,
      pageType,
    },
  };

  setPageDetails(page);
};

export const setPageInfoWithEventCategories = (
  primaryCategory: string,
  subCategory1: string,
  subCategory2: string,
  pageName: string,
  pageType: string,
) => {
  const page: IPageData = {
    category: {
      primaryCategory,
      subCategory1,
      subCategory2,
    },
    pageInfo: {
      pageName,
      pageType,
    },
  };

  setPageDetails(page);
};

export const setPageInfoWithEvent = (
  primaryCategory: string,
  subCategory1: string,
  subCategory2: string,
  pageName: string,
  pageType: string,
  pageLoadEvent: string,
) => {
  const page: IPageData = {
    category: {
      primaryCategory,
      subCategory1,
      subCategory2,
    },
    pageInfo: {
      pageName,
      pageType,
      pageLoadEvent,
    },
  };

  setPageDetails(page);
};

/**
 * Map payment method display/value or tender type to analytics account type code.
 */
export const getAnalyticsAccountType = (paymentMethod = ''): string => {
  const normalized = (paymentMethod || '').toUpperCase();
  const cardMap: Record<string, string> = {
    VISA: 'visa',
    VI: 'visa',
    MC: 'mc',
    AMEX: 'amex',
    DISC: 'disc',
    HDCON: 'hdcon',
    HDCOM: 'hdcom',
    PTC: 'protrade',
  };
  return cardMap[normalized];
};

// Tagging for when the user clicks on Start a Return Button
export const TagStartAReturn = (orderDetailsData: IOrderDetailsResponse) => {
  const exchange = getMilitaryExchange(orderDetailsData);

  // Determine account type: use isPTCOrder flag first, then tenders
  const accountType = orderDetailsData.isPTCOrder
    ? 'protrade'
    : getAnalyticsAccountType(orderDetailsData.tenders?.[0]?.type);

  const tagObject: IAnalyticsEvent = {
    category: { primaryCategory: 'button submission' },
    eventInfo: { eventName: 'start a return' },
    exchange,
    myAccount: {
      settings: `purchase history: order details: ${accountType}`,
    },
  };
  pushEventInfo(tagObject);
};

// Analytics used to capture when user clicks on the cancel return button regardless if it was successful or not
export const TriggerCancelSorisOnClickCancelAnalytics = () =>
  pushEventInfo({
    category: { primaryCategory: 'button submission' },
    eventInfo: { eventName: 'my account settings' },
    myAccount: {
      settings:
        'purchase history: order details: cancel this return overlay: yes cancel this return button',
    },
  });
// Analytics used to capture when user leaves the cancel return drawer without cancelling the return
export const TriggerCancelSorisOnAbandonAnalytics = () =>
  pushEventInfo({
    category: { primaryCategory: 'button submission' },
    eventInfo: { eventName: 'my account settings' },
    myAccount: {
      settings:
        'purchase history: order details: cancel this return overlay: no button or x button',
    },
  });
// Analytics used to capture when a soris cancel attempt was not successful
export const TriggerCancelSorisOnErrorAnalytics = () =>
  pushEventInfo({
    category: { primaryCategory: 'error' },
    eventInfo: {
      eventName: 'post page error',
      error: [
        {
          type: 'system',
          displayType: 'inline',
          pageSection: 'post page error',
          message: ERROR_MESSAGE,
          code: 'n/a',
        },
      ],
    },
  });
export const TriggerViewQRCodeAnalytics = (
  eventOrigin: 'order-details' | 'purchase-history' = 'order-details',
) =>
  pushEventInfo({
    category: {
      primaryCategory: 'overlay',
    },
    eventInfo: {
      eventName:
        eventOrigin === 'purchase-history'
          ? 'purchase history: in store return qr code'
          : 'order details: in store return qr code',
    },
  });

/**
 *****************************************************
 *  PRINT RETURN LABEL TAGGING
 *****************************************************
 */

/**
 * Tagging for when the user clicks the print return label button
 */
export const TagShippingLabelButtonSubmission = () => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings:
        'purchase history: order details: print free return label button',
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  UPS BARCODE TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user clicks the print return label button
 */
export const TagUPSBarcode = (isOrderDetails: boolean) => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings: isOrderDetails
        ? 'purchase history: order details: view ups barcode button'
        : 'purchase history: view ups barcode button',
    },
  };
  pushEventInfo(tagObject);
};

/**
 *****************************************************
 *  ADD ITEMS FAKE DOOR TAGGING
 *****************************************************
 */
/**
 * Tagging for when the user clicks the add items fake door button
 */
export const TagAddItemsFakeDoorButton = (
  page: 'order-details' | 'purchase-history',
) => {
  const tagObject: IAnalyticsEvent = {
    category: {
      primaryCategory: 'button submission',
    },
    eventInfo: {
      eventName: 'my account settings',
    },
    myAccount: {
      settings:
        page === 'order-details'
          ? 'purchase history: order details: add items to order button'
          : 'purchase history: add items to order button',
    },
  };
  pushEventInfo(tagObject);
};
